import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { useRouter } from "next/router";
import IconClose from "@/common/icons/close.svg";
import IconRightArrow from "@/common/icons/arrow-right.svg";
import IconSearch from "@/common/icons/search.svg";
import useLiveSearch from "@/common/hook/search/useLiveSearch";
import SearchBarLiveContainer from "@/components/common/SearchBar/SearchBarLiveContainer";

const SearchBar = ({ isInHeader, isDetailPage }) => {
  const router = useRouter();
  const [query, setQuery] = useState(isDetailPage ? router.query.q || "" : "");
  const { loading, data, search } = useLiveSearch(isDetailPage);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const canUseBlur = useRef(true);
  const replaceURL = useCallback(
    (newQuery) => {
      setQuery(newQuery);
      const queries = { ...router.query };
      if (newQuery.length === 0) {
        delete queries.q;
      } else {
        queries.q = newQuery;
      }

      router.push(
        {
          pathname: "/search",
          query: queries,
        },
        undefined,
        { shallow: isDetailPage }
      );
      if (!isDetailPage) setDropdownOpen(false);
    },
    [isDetailPage, router]
  );

  const [cursor, setCursor] = useState(-1);

  return (
    <form
      className={clsx(
        "flex relative gap-2 items-center min-w-0 bg-white rounded-lg border border-grey-400",
        isInHeader ? "py-1.5 px-2.5 w-64 lg:w-96 xl:w-[32rem] h-11" : "flex-grow md:p-6 py-2.5 px-3.5 h-full shadow-md"
      )}
      onSubmit={(e) => {
        e.preventDefault();

        if (!isDetailPage) replaceURL(query);
      }}
    >
      <IconSearch
        className={clsx(
          "flex-shrink-0 w-5 h-5 text-grey-500",
          isInHeader ? "mr-2 md:w-4 md:h-4" : "mr-3 md:w-6 md:h-6"
        )}
      />

      <input
        aria-label="Search"
        type="text"
        className={clsx("flex-grow w-full h-7 focus:outline-none", !isInHeader && "md:h-9 sm:text-xl")}
        value={query}
        onChange={({ target }) => {
          const searchQuery = target.value;
          setCursor(-1);
          if (isDetailPage) {
            replaceURL(searchQuery);
          } else {
            setQuery(searchQuery);
          }
          if (searchQuery.length >= 2) search(searchQuery);
        }}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setTimeout(() => setDropdownOpen(false), 100)}
        onKeyDown={(e) => {
          const length = data?.suggest?.length || 0;
          if (length > 0) {
            if (e.key === "ArrowDown") {
              setCursor((c) => (c < length ? c + 1 : c));
            }
            if (e.key === "ArrowUp") {
              setCursor((c) => (c > 0 ? c - 1 : 0));
            }
            if (e.key === "Enter" && cursor >= 0 && cursor < length) {
              e.preventDefault();
              replaceURL(data.suggest[cursor]);
            }
          }
        }}
      />
      <button
        type="button"
        className={clsx("flex-shrink-0 w-8 h-8 rounded-full button-icon", query.length === 0 && "hidden")}
        onClick={() => {
          setCursor(-1);
          if (isDetailPage) {
            replaceURL("");
          } else {
            setQuery("");
          }
        }}
        aria-label="Clear search input"
      >
        <IconClose className="w-4 h-4" />
      </button>
      {!isDetailPage && (
        <button type="submit" className="flex-shrink-0 w-8 h-8 rounded-full button-icon" aria-label="Search">
          <IconRightArrow className="w-4 h-4" />
        </button>
      )}

      <SearchBarLiveContainer
        cursor={cursor}
        loading={loading}
        data={data}
        isOpen={isDropdownOpen}
        closeRoutingOnBlur={() => {
          canUseBlur.current = false;
        }}
        search={(q) => replaceURL(q)}
        responsive={!isInHeader}
      />
    </form>
  );
};

export default SearchBar;
